<script setup lang="ts">
import {
  breakpointsTailwind,
  useBreakpoints,
  useElementBounding,
  useEventListener,
} from '@vueuse/core';
import { connectCurrentRefinements } from 'instantsearch.js/es/connectors';
import {
  CurrentRefinementsConnectorParams,
  CurrentRefinementsWidgetDescription,
} from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import { SortByItem } from 'instantsearch.js/es/connectors/sort-by/connectSortBy';
import { computed, onMounted, PropType, ref, watch } from 'vue';

import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useWidget } from '@/composables/instant-search/useWidget';

const props = defineProps({
  // no default because included and excluded are incompatible
  excludedAttributes: {
    required: false,
    type: Array as PropType<CurrentRefinementsConnectorParams['excludedAttributes']>,
  },
  // no default because included and excluded are incompatible
  includedAttributes: {
    required: false,
    type: Array as PropType<CurrentRefinementsConnectorParams['includedAttributes']>,
  },
  isAbbreviated: { required: false, type: Boolean, default: false },
  separateFilterSort: { required: false, type: Boolean, default: false },
  sortApplied: { required: false, type: String },
  sortOptions: { required: false, type: Array as PropType<SortByItem[]> },
});

defineEmits(['open-filters', 'open-separated-filters', 'open-sort-by']);

const widgetParams = computed<CurrentRefinementsConnectorParams>(() => ({
  excludedAttributes: props.excludedAttributes,
  includedAttributes: props.includedAttributes,
}));

const { state } = useWidget<CurrentRefinementsWidgetDescription, CurrentRefinementsConnectorParams>(
  { connector: connectCurrentRefinements },
  widgetParams,
);

const noRefinement = computed(() => state.value?.items.length === 0);
const refinementCount = computed(
  () => state.value?.items.reduce((count, { refinements }) => count + refinements.length, 0) ?? 0,
);
const refinementText = computed(() => {
  let content = `${refinementCount.value}`;
  if (!props.isAbbreviated) content += ' Applied';
  return `(${content})`;
});

const sortText = computed(
  () => props.sortOptions?.find((item) => item.value === props.sortApplied)?.label ?? 'Popularity',
);

const breakpoints = useBreakpoints({ ...breakpointsTailwind, 'ws-md': 892 });
const isMobile = breakpoints.smaller('lg');
const mobileFilters = ref<HTMLElement>();

const mobileHeaderHeight = ref<number>(0);
const mobileMenuOffset = computed(() => {
  if (!mobileHeaderHeight.value) return '5.75rem';
  return `${mobileHeaderHeight.value}px`;
});
const mobileFiltersClass = ref();

const updateMobileFiltersPosition = () => {
  if (!isMobile.value) return;
  const { top } = useElementBounding(mobileFilters);
  if (top.value <= mobileHeaderHeight.value) {
    mobileFiltersClass.value = `custom-fixed fixed inset-x-0 px-3 py-3 bg-white shadow`;
  }
  if (window.scrollY < mobileHeaderHeight.value) {
    mobileFiltersClass.value = '';
  }
};

useEventListener('scroll', updateMobileFiltersPosition);

onMounted(() => {
  watch(
    isMobile,
    (val) => {
      if (val) {
        const mobileHeader = document.querySelector<HTMLElement>('#appHeader');
        mobileHeaderHeight.value = mobileHeader?.offsetHeight ?? 0;
      }
    },
    { immediate: true },
  );
  updateMobileFiltersPosition();
});
</script>

<template>
  <div
    v-if="separateFilterSort"
    ref="mobileFilters"
    :class="mobileFiltersClass"
    data-test="reveal-filters-mobile"
  >
    <div class="flex gap-2">
      <UnstyledButton class="button-custom" @click="$emit('open-separated-filters')">
        Filter <span v-if="!noRefinement">&nbsp;{{ refinementText }}</span>
      </UnstyledButton>
      <UnstyledButton class="button-custom" @click="$emit('open-sort-by')">
        Sort By<span v-if="!!sortText">: {{ sortText }}</span>
      </UnstyledButton>
    </div>
  </div>

  <button
    v-else
    class="reveal-filters"
    @click.prevent="$emit('open-filters')"
    data-test="reveal-filters-mobile"
  >
    <slot>Filter &amp; Sort</slot>
    <span v-if="!noRefinement">&nbsp;{{ refinementText }}</span>
  </button>
</template>

<style lang="scss" scoped>
.button-custom {
  background-color: #eaeaea;
  font-size: 12px;
  @apply flex items-center justify-center tracking-wide font-proxima-nova rounded-full border-none w-full h-8;
}

.custom-fixed {
  top: v-bind('mobileMenuOffset');
  z-index: 35;
}
</style>
